import { useRef, useEffect, useState } from 'react';
import { Crown, Sparkles, Send, Trash2, User, Mail, Paperclip, X, FileText, Square, Zap } from 'lucide-react';
import { brand } from '@iheart/shared';
import { cn } from '@iheart/ui';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useDominaChat, useDominaStatus, type DominaMessage } from '../../lib/domina';
import { MarkdownLite } from './MarkdownLite';
import { useStartDm } from '../../lib/chat';
import { useCurrentWorkspace } from '../../lib/workspace';
import { useChatWidgetStore } from './ChatWidgetStore';
import { WidgetAgent } from './WidgetAgent';
import { api } from '../../lib/api';
import { useQuery } from '@tanstack/react-query';

interface WorkspaceMember {
  publicId: string;
  name: string;
  email: string;
  role: string;
}

/**
 * Domina container: a toggle between the free **Agent** (closed, no tokens) and
 * the paid **AI** (LLM). Starts on Agent. A single clear button resets whichever
 * is active.
 */
export function WidgetDomina() {
  const mode = useChatWidgetStore((s) => s.dominaMode);
  const setMode = useChatWidgetStore((s) => s.setDominaMode);
  const [clearSignal, setClearSignal] = useState(0);

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <div className="flex items-center gap-2 border-b border-neutral-100 px-2 py-1.5">
        <div className="flex flex-1 rounded-lg bg-neutral-100 p-0.5 text-[11px] font-semibold">
          <button
            type="button"
            onClick={() => setMode('agent')}
            className={cn(
              'flex flex-1 items-center justify-center gap-1 rounded-md px-2 py-1 transition-colors',
              mode === 'agent' ? 'bg-white text-pink-600 shadow-sm' : 'text-neutral-500 hover:text-neutral-700',
            )}
          >
            <Sparkles className="size-3" /> Agent · free
          </button>
          <button
            type="button"
            onClick={() => setMode('ai')}
            className={cn(
              'flex flex-1 items-center justify-center gap-1 rounded-md px-2 py-1 transition-colors',
              mode === 'ai' ? 'text-white shadow-sm' : 'text-neutral-500 hover:text-neutral-700',
            )}
            style={mode === 'ai' ? { backgroundImage: brand.gradient.primary } : undefined}
          >
            <Crown className="size-3" /> AI · paid
          </button>
        </div>
        <button
          type="button"
          onClick={() => setClearSignal((s) => s + 1)}
          title="Clear conversation"
          className="rounded p-1 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-600"
        >
          <Trash2 className="size-3.5" />
        </button>
      </div>
      {mode === 'agent' ? <WidgetAgent clearSignal={clearSignal} /> : <WidgetDominaAI clearSignal={clearSignal} />}
    </div>
  );
}

function WidgetDominaAI({ clearSignal }: { clearSignal?: number }) {
  const { messages, sendMessage, isPending, stop, clearHistory } = useDominaChat();
  const [draft, setDraft] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const lastAssistantRef = useRef<HTMLDivElement>(null);
  const prevPendingRef = useRef(false);
  const store = useChatWidgetStore();
  const ws = useCurrentWorkspace();
  const startDm = useStartDm();
  const navigate = useNavigate();
  const dominaStatus = useDominaStatus();
  const blocked = dominaStatus.data?.blocked ?? false;

  // Clear when the container's clear button is pressed.
  const lastClear = useRef(clearSignal);
  useEffect(() => {
    if (clearSignal !== lastClear.current) {
      lastClear.current = clearSignal;
      clearHistory();
    }
  }, [clearSignal, clearHistory]);

  function goBilling() {
    store.close?.();
    navigate('/settings/billing');
  }

  // Fetch workspace members for "Chat with a human" feature
  const wsMembers = useQuery({
    queryKey: ['workspace-members', ws?.publicId],
    queryFn: () =>
      api<{ data: WorkspaceMember[] }>(
        `/workspaces/${ws!.publicId}/members`,
      ).then((r) => r.data),
    enabled: !!ws,
  });

  // Auto-scroll. While Domina is thinking (or the user just posted) keep the
  // view pinned to the bottom so the typing indicator shows. When her answer
  // arrives, jump to the START of that answer so the user reads from the top
  // and scrolls down as they read — instead of landing at the very end.
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    const last = messages[messages.length - 1];
    const justAnswered = prevPendingRef.current && !isPending && last?.role === 'assistant';
    prevPendingRef.current = isPending;
    if (justAnswered && lastAssistantRef.current) {
      lastAssistantRef.current.scrollIntoView({ block: 'start' });
    } else {
      container.scrollTop = container.scrollHeight;
    }
  }, [messages, isPending]);

  // Index of the newest assistant message — its top is our scroll target.
  let lastAssistantIndex = -1;
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i]!.role === 'assistant') {
      lastAssistantIndex = i;
      break;
    }
  }

  function handlePickFiles(list: FileList | null) {
    if (!list || list.length === 0) return;
    const incoming = Array.from(list);
    const MAX_BYTES = 15 * 1024 * 1024; // per file
    const MAX_TOTAL = 18 * 1024 * 1024; // keep the whole request under the 25MB body limit
    const tooBig = incoming.filter((f) => f.size > MAX_BYTES);
    if (tooBig.length) {
      toast.error(`${tooBig.length === 1 ? tooBig[0]!.name : `${tooBig.length} files`} exceed the 15MB limit.`);
    }
    setFiles((prev) => {
      const next = [...prev];
      let total = prev.reduce((sum, f) => sum + f.size, 0);
      for (const f of incoming) {
        if (f.size > MAX_BYTES) continue;
        if (next.length >= 5) break;
        if (total + f.size > MAX_TOTAL) {
          toast.error('Attachments are too large — keep the total under ~18MB.');
          break;
        }
        next.push(f);
        total += f.size;
      }
      return next;
    });
  }

  async function handleSend() {
    if (blocked) {
      goBilling();
      return;
    }
    const text = draft.trim();
    if ((!text && files.length === 0) || isPending) return;
    const attached = files;
    setDraft('');
    setFiles([]);
    try {
      await sendMessage({ text: text || 'Please take a look at the attached file(s).', files: attached });
    } catch {
      // Error handled by mutation
    }
    inputRef.current?.focus();
  }

  // Interrupt Domina mid-thought and drop the prompt back for editing.
  function handleStop() {
    const restored = stop();
    if (restored) setDraft(restored);
    inputRef.current?.focus();
  }

  async function handleChatWithHuman() {
    if (!wsMembers.data) return;
    // Find an OWNER or ADMIN to DM
    const human =
      wsMembers.data.find((m) => m.role === 'OWNER') ??
      wsMembers.data.find((m) => m.role === 'ADMIN') ??
      wsMembers.data[0];
    if (!human) return;

    try {
      const result = await startDm.mutateAsync(human.publicId);
      store.goToThread(result.publicId);
    } catch {
      // Fallback: just go to new chat
      store.goToNewChat();
    }
  }

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3">
        {/* Greeting (always shown at top) */}
        <div className="mb-3 flex gap-2">
          <div
            className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full text-white"
            style={{ backgroundImage: brand.gradient.primary }}
          >
            <Crown className="size-3.5" />
          </div>
          <div className="space-y-1">
            <div className="rounded-xl rounded-bl-sm border border-pink-100/50 bg-gradient-to-br from-pink-50 to-indigo-50 px-3 py-2 text-sm text-neutral-800">
              <strong>Howzit!</strong> I'm Domina, your very own personal AI Assistant. What are we sorting out today?
            </div>
          </div>
        </div>

        {/* Conversation */}
        {messages.map((msg, idx) => (
          <div key={msg.id} ref={idx === lastAssistantIndex ? lastAssistantRef : undefined} style={{ scrollMarginTop: 8 }}>
            <DominaBubble message={msg} onChatWithHuman={handleChatWithHuman} />
          </div>
        ))}

        {/* Typing indicator */}
        {isPending && (
          <div className="mb-2 flex gap-2">
            <div
              className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full text-white"
              style={{ backgroundImage: brand.gradient.primary }}
            >
              <Crown className="size-3.5" />
            </div>
            <div className="rounded-xl rounded-bl-sm border border-pink-100/50 bg-gradient-to-br from-pink-50 to-indigo-50 px-4 py-3">
              <div className="flex gap-1">
                <span className="size-2 animate-bounce rounded-full bg-pink-400 [animation-delay:0ms]" />
                <span className="size-2 animate-bounce rounded-full bg-pink-400 [animation-delay:150ms]" />
                <span className="size-2 animate-bounce rounded-full bg-pink-400 [animation-delay:300ms]" />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Compose — or the out-of-credits call-to-action */}
      {blocked ? (
        <div className="border-t border-neutral-100 p-3">
          <div className="rounded-xl border border-pink-100 bg-gradient-to-br from-pink-50 to-indigo-50 p-3 text-center">
            <div
              className="mx-auto mb-1.5 flex size-8 items-center justify-center rounded-full text-white"
              style={{ backgroundImage: brand.gradient.primary }}
            >
              <Zap className="size-4" />
            </div>
            <p className="text-sm font-semibold text-neutral-800">You're out of AI credits</p>
            <p className="mt-0.5 text-xs text-neutral-500">
              Top up to keep chatting with Domina and using AI across CNTRL.
            </p>
            <button
              type="button"
              onClick={goBilling}
              className="mt-2.5 w-full rounded-lg py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90"
              style={{ backgroundImage: brand.gradient.primary }}
            >
              Buy tokens now
            </button>
          </div>
        </div>
      ) : (
      <div className="border-t border-neutral-100 p-2">
        {/* Selected file chips */}
        {files.length > 0 && (
          <div className="mb-1.5 flex flex-wrap gap-1.5">
            {files.map((f, i) => (
              <span
                key={`${f.name}-${i}`}
                className="flex max-w-[180px] items-center gap-1 rounded-lg border border-pink-100 bg-pink-50 px-2 py-1 text-[11px] text-neutral-700"
              >
                <FileText className="size-3 shrink-0 text-pink-500" />
                <span className="truncate">{f.name}</span>
                <button
                  type="button"
                  onClick={() => setFiles((prev) => prev.filter((_, idx) => idx !== i))}
                  className="shrink-0 text-neutral-400 hover:text-neutral-700"
                  title="Remove"
                >
                  <X className="size-3" />
                </button>
              </span>
            ))}
          </div>
        )}
        <div className="flex items-end gap-1.5">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,.pdf,.doc,.docx,.txt,.csv,.tsv,.md,.json"
            className="hidden"
            onChange={(e) => {
              handlePickFiles(e.target.files);
              e.target.value = '';
            }}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={isPending || files.length >= 5}
            className="flex size-9 shrink-0 items-center justify-center rounded-lg border border-neutral-200 bg-white text-neutral-500 transition-colors hover:border-pink-300 hover:text-pink-500 disabled:opacity-50"
            title="Attach a file (image, PDF, Word, CSV, text)"
          >
            <Paperclip className="size-4" />
          </button>
          <textarea
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            placeholder="Ask Domina anything..."
            rows={7}
            className="flex-1 resize-none overflow-y-auto rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm leading-5 outline-none focus:border-pink-300 focus:ring-1 focus:ring-pink-100"
          />
          {isPending ? (
            <button
              type="button"
              onClick={handleStop}
              title="Stop Domina and edit your message"
              className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-neutral-800 text-white transition-colors hover:bg-neutral-700"
            >
              <Square className="size-3.5" fill="currentColor" />
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSend}
              disabled={!draft.trim() && files.length === 0}
              className="flex size-9 shrink-0 items-center justify-center rounded-lg text-white transition-colors disabled:opacity-50"
              style={{ backgroundImage: brand.gradient.primary }}
            >
              <Send className="size-4" />
            </button>
          )}
        </div>
      </div>
      )}
    </div>
  );
}

function DominaBubble({
  message,
  onChatWithHuman,
}: {
  message: DominaMessage;
  onChatWithHuman: () => void;
}) {
  const isUser = message.role === 'user';
  const time = new Date(message.timestamp).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });

  if (isUser) {
    return (
      <div className="mb-2 flex justify-end">
        <div className="max-w-[80%] space-y-0.5">
          <div className="rounded-xl rounded-br-sm bg-pink-500 px-3 py-2 text-sm text-white">
            {message.content}
          </div>
          {message.attachments && message.attachments.length > 0 && (
            <div className="flex flex-wrap justify-end gap-1">
              {message.attachments.map((a, i) => (
                <span
                  key={`${a.name}-${i}`}
                  className="flex max-w-[160px] items-center gap-1 rounded-md bg-pink-100 px-1.5 py-0.5 text-[10px] text-pink-700"
                >
                  <FileText className="size-2.5 shrink-0" />
                  <span className="truncate">{a.name}</span>
                </span>
              ))}
            </div>
          )}
          <span className="block text-right text-[10px] text-neutral-400">{time}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-2 flex gap-2">
      <div
        className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full text-white"
        style={{ backgroundImage: brand.gradient.primary }}
      >
        <Crown className="size-3.5" />
      </div>
      <div className="max-w-[85%] space-y-1">
        <div className="rounded-xl rounded-bl-sm border border-pink-100/50 bg-gradient-to-br from-pink-50 to-indigo-50 px-3 py-2 text-sm leading-relaxed text-neutral-800">
          <MarkdownLite content={message.content} />
          {message.images && message.images.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {message.images.map((img) => (
                <a
                  key={img.publicId}
                  href={img.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                  title={`${img.filename} — open full size`}
                >
                  <img
                    src={img.url}
                    alt={img.filename}
                    loading="lazy"
                    className="max-h-44 max-w-full rounded-lg border border-pink-100 object-cover shadow-sm transition-transform hover:scale-[1.02]"
                  />
                </a>
              ))}
            </div>
          )}
        </div>

        {/* Fallback buttons when Domina isn't confident */}
        {message.confident === false && (
          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={onChatWithHuman}
              className="flex items-center gap-1 rounded-lg border border-neutral-200 bg-white px-2.5 py-1.5 text-[11px] font-medium text-neutral-700 transition-colors hover:border-pink-300 hover:bg-pink-50"
            >
              <User className="size-3" /> Chat with a human
            </button>
            <a
              href="mailto:hello@iheartdigital.co.za"
              className="flex items-center gap-1 rounded-lg border border-neutral-200 bg-white px-2.5 py-1.5 text-[11px] font-medium text-neutral-700 transition-colors hover:border-pink-300 hover:bg-pink-50"
            >
              <Mail className="size-3" /> Send email
            </a>
          </div>
        )}

        <span className="block text-[10px] text-neutral-400">{time}</span>
      </div>
    </div>
  );
}
